// ============================================================
// BadgeRow - one line of badges under the health bar (plan plate-unboxed).
//
// The rails beside the plate became rows, so the figure has the whole
// width. What does not fit the line folds into a "+N" button, which opens
// the full list (Escape or a press outside closes it; that Escape goes no
// further, so it never also cancels the attack or closes the panel).
//
// FOLDED ITEMS STAY LAID OUT, hidden (`data-folded`: visibility hidden,
// inert), never removed: their positions are what the fold measures, so a
// resize measures the same line every time instead of a line it already
// cut. The measuring is a ResizeObserver on the line; without one (jsdom)
// nothing folds.
// ============================================================
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import { useCloseOnOutsidePointer } from './characterLibrary/useLibraryPanel';
import { foldOf, sameFold, type Fold } from './badgeRowFold';
import styles from '../styles/BadgeRow.module.css';

interface Props {
  /** What the row holds, for the "+N" button's and the list's accessible names ("Conditions"). */
  label: string;
  /** The line: items, or groups marked `data-row-group` whose children are the items. */
  children: ReactNode;
  /** The full list the "+N" opens: the same items again, without the line's test ids. */
  full: ReactNode;
}

/** The line's items in order: its children, a marked group's children in the group's place. */
function itemsOf(line: HTMLElement): (typeof line.children)[number][] {
  return [...line.children].flatMap((child) => (child.hasAttribute('data-row-group') ? [...child.children] : [child]));
}

/**
 * Measure the line, mark the folded items, and say the fold. Edges come from bounding boxes against the line's own
 * left: a condition icon is an <svg>, which has no offsetLeft (the first build measured it as nothing and folded
 * every icon away).
 */
function measure(line: HTMLElement): Fold | null {
  const items = itemsOf(line);
  const left = line.getBoundingClientRect().left;
  const fold = foldOf(
    items.map((el) => el.getBoundingClientRect().right - left),
    line.clientWidth,
  );
  items.forEach((el, i) => {
    const folded = !!fold && i >= fold.shown;
    el.toggleAttribute('data-folded', folded);
    el.toggleAttribute('inert', folded);
  });
  return fold;
}

/**
 * The fold of the line, kept up to date: measured again whenever its items change, and by one observer (made once,
 * for the row's life) whenever its width does.
 */
function useFold(children: ReactNode) {
  const line = useRef<HTMLDivElement>(null);
  const [fold, setFold] = useState<Fold | null>(null);
  const update = useCallback(() => {
    if (!line.current || !globalThis.ResizeObserver) return;
    const next = measure(line.current);
    setFold((was) => (sameFold(was, next) ? was : next));
  }, []);
  useLayoutEffect(update, [children, update]);
  useLayoutEffect(() => {
    const Observer = globalThis.ResizeObserver;
    if (!line.current || !Observer) return undefined;
    const observer = new Observer(update);
    observer.observe(line.current);
    return () => observer.disconnect();
  }, [update]);
  return { line, fold };
}

export default function BadgeRow({ label, children, full }: Props) {
  const { line, fold } = useFold(children);
  const wrap = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Nothing folded, nothing to list: the list closes with its button and stays shut until asked again.
  if (open && !fold) setOpen(false);
  const close = useCallback(() => setOpen(false), []);
  useCloseOnOutsidePointer(open, wrap, close);
  useEscapeKey(open, close);
  return (
    <div className={styles.row} ref={wrap}>
      <div className={styles.line} ref={line} data-row-line="">
        {children}
      </div>
      {fold && (
        <button
          type="button"
          className={styles.more}
          style={{ left: fold.at }}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={`${label}: ${fold.hidden} more`}
          data-testid="row-more"
          onClick={() => setOpen((was) => !was)}
        >
          +{fold.hidden}
        </button>
      )}
      {open && (
        <div className={styles.list} role="dialog" aria-label={label} data-testid="row-more-list">
          {full}
        </div>
      )}
    </div>
  );
}
